import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Bot, Building2, CheckCheck, ShieldAlert } from 'lucide-react';
import api, { errorText } from '../services/api';
import { useApi } from '../lib/useApi';
import { useAccess } from '../context/AuthContext';
import { ago, date, num } from '../lib/format';
import { Button, Card, Chip, Empty, ErrorBanner, PageHeader, PageSkeleton, SearchBox, Segmented, Stat, useToast } from '../components/ui';

interface SecurityEvent {
  id: number; tenant_id: string; tenant_name: string | null; user_id: string | null;
  user_name: string | null; user_email: string | null; question: string; created_at: string;
  reviewed: boolean; reviewed_by: string | null; reviewed_at: string | null;
}
interface Totals { total: number; unreviewed: number; last_24h: number; tenants: number }

/** Suspicious questions sent to FluxPilot (FluxWatch's chat assistant) -- jailbreak/prompt-injection
    attempts the model was told to decline, flagged here so the platform team can see who tried what.
    FluxWatch's own customers never see this: the tenant whose account tried it has no way to know. */
export default function AssistantSecurity() {
  const toast = useToast();
  const { canWrite } = useAccess();
  const [view, setView] = useState<'all' | 'unreviewed' | 'reviewed'>('unreviewed');
  const [search, setSearch] = useState('');
  const [busyAll, setBusyAll] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const { data, error, loading, reload } = useApi<{ events: SecurityEvent[]; totals: Totals }>('/assistant-security-events');

  const all = data?.events || [];
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter(e => {
      if (view === 'unreviewed' && e.reviewed) return false;
      if (view === 'reviewed' && !e.reviewed) return false;
      if (q && ![e.tenant_name, e.user_name, e.user_email, e.question].join(' ').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [all, view, search]);

  const markReviewed = async (id: number) => {
    setBusyId(id);
    try { await api.post(`/assistant-security-events/${id}/review`); toast('ok', 'Marked reviewed'); reload(); }
    catch (e) { toast('error', errorText(e)); } finally { setBusyId(null); }
  };

  const markAllReviewed = async () => {
    setBusyAll(true);
    try { const res = await api.post('/assistant-security-events/mark-all-reviewed', {}); toast('ok', `Marked ${res.data.count} event(s) reviewed`); reload(); }
    catch (e) { toast('error', errorText(e)); } finally { setBusyAll(false); }
  };

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorBanner text={error} onRetry={reload} />;
  const t = data!.totals;

  return (
    <div>
      <PageHeader title="FluxPilot security" subtitle="Questions sent to FluxWatch's chat assistant that looked like a jailbreak or prompt-injection attempt -- the model was instructed to decline each one, never answer it. The tenant that tried it is never shown this; it's not written to their own audit log."
        actions={canWrite && t.unreviewed > 0 ? <Button icon={CheckCheck} loading={busyAll} onClick={markAllReviewed}>Mark all reviewed</Button> : undefined} />

      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Unreviewed" value={num(t.unreviewed)} sub={t.unreviewed ? 'needs a look' : 'all caught up'} icon={ShieldAlert} tone={t.unreviewed ? 'red' : 'green'} />
        <Stat label="Total events" value={num(t.total)} sub="flagged, never answered" icon={Bot} tone="slate" />
        <Stat label="Last 24 hours" value={num(t.last_24h)} icon={AlertTriangle} tone="amber" />
        <Stat label="Tenants affected" value={num(t.tenants)} icon={Building2} tone="purple" />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Segmented value={view} onChange={setView} options={[
          { key: 'all', label: 'All', count: all.length },
          { key: 'unreviewed', label: 'Unreviewed', count: all.filter(e => !e.reviewed).length },
          { key: 'reviewed', label: 'Reviewed', count: all.filter(e => e.reviewed).length },
        ]} />
        <div className="ml-auto"><SearchBox value={search} onChange={setSearch} placeholder="Search tenant, user or question" /></div>
      </div>

      <Card bodyClass="p-0">
        {rows.length === 0 ? (
          <Empty icon={ShieldAlert} title={all.length ? 'No events match' : 'No suspicious questions yet'}
            text={all.length ? 'Try a different view or search.' : "They'll show up here the moment FluxPilot flags one."} />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="border-b border-slate-100 bg-slate-50/60">
                <tr>{['Tenant', 'User', 'Question', 'When', 'Status', ''].map(h =>
                  <th key={h} className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-medium text-slate-500">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(e => (
                  <tr key={e.id} className="align-top hover:bg-slate-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {e.tenant_name ? <Link to={`/tenants/${e.tenant_id}`} className="font-medium text-blue-600 hover:underline">{e.tenant_name}</Link>
                        : <span className="text-slate-400">Unknown tenant</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <div className="font-medium text-slate-900">{e.user_name || '—'}</div>
                      <div className="text-xs text-slate-500">{e.user_email || '—'}</div>
                    </td>
                    <td className="max-w-md px-4 py-3 text-sm text-slate-700">
                      <div className="line-clamp-3 whitespace-pre-wrap" title={e.question}>{e.question}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{ago(e.created_at)}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {e.reviewed
                        ? <Chip tone="green" dot>Reviewed{e.reviewed_by ? ` · ${e.reviewed_by}` : ''}</Chip>
                        : <Chip tone="red" dot>Unreviewed</Chip>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {!e.reviewed && canWrite && (
                        <Button size="sm" loading={busyId === e.id} onClick={() => markReviewed(e.id)}>Mark reviewed</Button>
                      )}
                      {e.reviewed && e.reviewed_at && <span className="text-xs text-slate-400">{date(e.reviewed_at)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
